import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaChartLine, 
  FaClock, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaHome,
  FaHospital,
  FaDownload,
  FaFileAlt,
  FaTachometerAlt
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  AreaChart,
  Area
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import styles from './EmployeeStatistics.module.css';

const COLORS = ['#4299e1', '#9f7aea', '#68d391', '#f6ad55', '#fc8181', '#4fd1c5', '#ed8936', '#48bb78'];

const EmployeeStatistics = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { employee, employeeStats: passedStats, visits: passedVisits, dateRange } = location.state || {};

  const [loading, setLoading] = useState(!passedVisits); // Don't load if we already have visits
  const [visits, setVisits] = useState(passedVisits || []);
  const [dateStart, setDateStart] = useState(dateRange?.start || '');
  const [dateEnd, setDateEnd] = useState(dateRange?.end || '');

  useEffect(() => {
    if (!employee) {
      toast.error('Çalışan bilgisi bulunamadı');
      navigate(-1);
      return;
    }
    
    // Only fetch if we don't have passed visits
    if (!passedVisits) {
      fetchEmployeeData();
    }
  }, [employee, dateStart, dateEnd]);

  const fetchEmployeeData = async () => {
    if (!employee?.id) return;
    
    setLoading(true);
    try {
      const visitsRef = collection(db, 'Visits');
      const visitsQuery = query(visitsRef, where('employeeId', '==', employee.id));
      const visitsSnapshot = await getDocs(visitsQuery);
      
      let visitsData = visitsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply date filter
      if (dateStart || dateEnd) {
        visitsData = visitsData.filter(visit => {
          const visitDate = visit.date || visit.scheduledDate || visit.visitStartDate;
          if (!visitDate) return false;
          
          if (dateStart && visitDate < dateStart) return false;
          if (dateEnd && visitDate > dateEnd) return false;
          return true;
        });
      }

      setVisits(visitsData);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error('Çalışan verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics using the same logic as SMO Lead Reports
  // If we have passedStats from SMO Lead Reports, use those values directly
  const statistics = useMemo(() => {
    if (!visits.length) return null;

    // If we have pre-calculated stats from SMO Lead Reports, use them as base
    if (passedStats) {
      // Still calculate some additional metrics from visits for charts
      const monthlyData = {};
      visits.forEach(v => {
        const date = v.date || v.scheduledDate || v.firstVisitDate;
        if (!date) return;
        
        const month = date.substring(0, 7);
        if (!monthlyData[month]) {
          monthlyData[month] = { home: 0, site: 0, total: 0, issues: 0 };
        }
        
        if (v.visitType === 'home') {
          monthlyData[month].home++;
        } else {
          monthlyData[month].site++;
        }
        monthlyData[month].total++;
        
        if (v.visitEvaluation?.issueOccurred === 'yes') {
          monthlyData[month].issues++;
        }
      });

      const weeklyData = {};
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - (i * 7));
        const weekKey = weekStart.toISOString().substring(0, 10);
        weeklyData[weekKey] = 0;
      }

      visits.forEach(v => {
        const date = v.date || v.scheduledDate || v.firstVisitDate;
        if (!date) return;
        
        const visitDate = new Date(date);
        const weekStart = new Date(visitDate);
        weekStart.setDate(visitDate.getDate() - visitDate.getDay());
        const weekKey = weekStart.toISOString().substring(0, 10);
        
        if (weeklyData.hasOwnProperty(weekKey)) {
          weeklyData[weekKey]++;
        }
      });

      const dailyDistribution = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
      visits.forEach(v => {
        const date = v.date || v.scheduledDate || v.firstVisitDate;
        if (!date) return;
        
        const dayOfWeek = new Date(date).getDay();
        dailyDistribution[dayOfWeek]++;
      });

      const hourlyDistribution = {};
      for (let i = 0; i < 24; i++) {
        hourlyDistribution[i] = 0;
      }
      
      visits.forEach(v => {
        const time = v.startTime || v.visitStartTime;
        if (!time) return;
        
        const hour = parseInt(time.split(':')[0]);
        if (!isNaN(hour) && hour >= 0 && hour < 24) {
          hourlyDistribution[hour]++;
        }
      });

      const issueCategories = {};
      visits.forEach(v => {
        if (v.visitEvaluation?.issueOccurred === 'yes' && v.visitEvaluation?.issueCategory) {
          const cat = v.visitEvaluation.issueCategory;
          issueCategories[cat] = (issueCategories[cat] || 0) + 1;
        }
      });

      // Calculate time metrics from visits
      const totalTravelTime = visits.reduce((sum, v) => {
        const visitEval = v.visitEvaluation;
        if (!visitEval) return sum;
        
        const before = parseInt(visitEval.travelTimeBefore) || 0;
        const after = parseInt(visitEval.travelTimeAfter) || 0;
        
        return sum + before + after;
      }, 0);

      const totalVisitTime = visits.reduce((sum, v) => {
        const visitEval = v.visitEvaluation;
        if (!visitEval) return sum;
        
        const hours = parseInt(visitEval.visitDurationHours) || 0;
        const minutes = parseInt(visitEval.visitDurationMinutes) || 0;
        
        return sum + (hours * 60) + minutes;
      }, 0);

      // Use passed stats and add calculated metrics
      return {
        totalVisits: passedStats.totalVisits,
        homeVisits: passedStats.homeVisits,
        siteVisits: passedStats.siteVisits,
        slaMetVisits: Math.round((passedStats.slaMetPct / 100) * passedStats.totalVisits),
        slaPercentage: passedStats.slaMetPct,
        issueVisits: visits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length,
        deviationVisits: passedStats.deviationCount,
        ecrfCompleted: Math.round((passedStats.ecrfPct / 100) * passedStats.totalVisits),
        ecrfPercentage: passedStats.ecrfPct,
        sourceFinalized: Math.round((passedStats.sourcePct / 100) * passedStats.totalVisits),
        sourcePercentage: passedStats.sourcePct,
        totalKm: passedStats.totalKm,
        avgKmPerVisit: passedStats.avgKmPerVisit,
        totalTravelTime: Math.round(totalTravelTime),
        avgTravelTimePerVisit: passedStats.totalVisits > 0 ? Math.round(totalTravelTime / passedStats.totalVisits) : 0,
        totalVisitTime: Math.round(totalVisitTime),
        avgVisitTimePerVisit: passedStats.totalVisits > 0 ? Math.round(totalVisitTime / passedStats.totalVisits) : 0,
        performanceScore: passedStats.performanceScore,
        overdueCount: passedStats.overdueCount,
        capaOpenedCount: passedStats.capaOpenedCount || 0,
        screenFailCount: passedStats.screenFailCount || 0,
        dropPatientCount: passedStats.dropPatientCount || 0,
        aeSaeCount: visits.filter(v => v.visitEvaluation?.wasAeSaeTaken === 'yes').length,
        dcfCount: visits.filter(v => v.visitEvaluation?.dcfCreated === 'yes').length,
        missedCount: visits.filter(v => v.visitEvaluation?.issueResultedInMiss === 'yes').length,
        deviationPct: passedStats.deviationPct || 0,
        monthlyData,
        weeklyData,
        dailyDistribution,
        hourlyDistribution,
        issueCategories
      };
    }

    // If no passed stats, calculate everything from scratch
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;
    const r1 = n => Math.round(n * 10) / 10;

    const total = visits.length;
    const homeV = visits.filter(v => v.visitType === 'home');
    const siteV = visits.filter(v => v.visitType !== 'home');

    // KM (only travelDistanceBefore — matching SMO Lead Reports)
    const totalKm = visits.reduce((sum, v) => {
      const ev = v.visitEvaluation || {};
      return sum + (parseFloat(ev.travelDistanceBefore) || 0);
    }, 0);
    const avgKmPerVisit = r1(total > 0 ? totalKm / total : 0);

    // eCRF & Source completion
    const ecrfDone = visits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
    const ecrfPct = pct(ecrfDone, total);
    const sourceDone = visits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
    const sourcePct = pct(sourceDone, total);

    // Deviations
    const deviationCount = visits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const deviationPct = r1(total > 0 ? deviationCount / total * 100 : 0);

    // CAPA Opened
    const capaOpenedCount = visits.filter(v => v.visitEvaluation?.capaOpened === 'yes').length;

    // Screen Fail / Drop Patient
    const screenFailCount = visits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
    const dropPatientCount = visits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;

    // SLA (matching SMO Lead Reports calculation)
    const withSLA = visits.filter(v =>
      v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
    );
    const slaMet = withSLA.filter(v => {
      const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
      return cycle <= parseFloat(v.visitEvaluation.slaTarget);
    }).length;
    const slaMetPct = pct(slaMet, withSLA.length);
    const overdueCount = withSLA.length - slaMet;

    // Issues
    const issueCount = visits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const issueScore = Math.max(0, 100 - pct(issueCount, total) * 5);

    // Performance Score (matching SMO Lead Reports formula)
    const slaScore = slaMetPct;
    const ecrfScore = ecrfPct;
    const sourceScore = sourcePct;
    const deviationScore = Math.max(0, 100 - deviationPct * 10);
    const performanceScore = Math.round(
      slaScore * 0.30 + ecrfScore * 0.20 + sourceScore * 0.20 +
      deviationScore * 0.15 + issueScore * 0.15
    );

    // AE/SAE and DCF
    const aeSaeCount = visits.filter(v => v.visitEvaluation?.wasAeSaeTaken === 'yes').length;
    const dcfCount = visits.filter(v => v.visitEvaluation?.dcfCreated === 'yes').length;
    const missedCount = visits.filter(v => v.visitEvaluation?.issueResultedInMiss === 'yes').length;

    // Time calculations (for additional metrics)
    const totalTravelTime = visits.reduce((sum, v) => {
      const visitEval = v.visitEvaluation;
      if (!visitEval) return sum;
      
      const before = parseInt(visitEval.travelTimeBefore) || 0;
      const after = parseInt(visitEval.travelTimeAfter) || 0;
      
      return sum + before + after;
    }, 0);

    const totalVisitTime = visits.reduce((sum, v) => {
      const visitEval = v.visitEvaluation;
      if (!visitEval) return sum;
      
      const hours = parseInt(visitEval.visitDurationHours) || 0;
      const minutes = parseInt(visitEval.visitDurationMinutes) || 0;
      
      return sum + (hours * 60) + minutes;
    }, 0);

    // Monthly distribution
    const monthlyData = {};
    visits.forEach(v => {
      const date = v.date || v.scheduledDate || v.firstVisitDate;
      if (!date) return;
      
      const month = date.substring(0, 7); // YYYY-MM
      if (!monthlyData[month]) {
        monthlyData[month] = { home: 0, site: 0, total: 0, issues: 0 };
      }
      
      if (v.visitType === 'home') {
        monthlyData[month].home++;
      } else {
        monthlyData[month].site++;
      }
      monthlyData[month].total++;
      
      if (v.visitEvaluation?.issueOccurred === 'yes') {
        monthlyData[month].issues++;
      }
    });

    // Weekly workload (last 12 weeks)
    const weeklyData = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - (i * 7));
      const weekKey = weekStart.toISOString().substring(0, 10);
      weeklyData[weekKey] = 0;
    }

    visits.forEach(v => {
      const date = v.date || v.scheduledDate || v.firstVisitDate;
      if (!date) return;
      
      const visitDate = new Date(date);
      const weekStart = new Date(visitDate);
      weekStart.setDate(visitDate.getDate() - visitDate.getDay());
      const weekKey = weekStart.toISOString().substring(0, 10);
      
      if (weeklyData.hasOwnProperty(weekKey)) {
        weeklyData[weekKey]++;
      }
    });

    // Daily workload distribution
    const dailyDistribution = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    visits.forEach(v => {
      const date = v.date || v.scheduledDate || v.firstVisitDate;
      if (!date) return;
      
      const dayOfWeek = new Date(date).getDay();
      dailyDistribution[dayOfWeek]++;
    });

    // Hourly distribution
    const hourlyDistribution = {};
    for (let i = 0; i < 24; i++) {
      hourlyDistribution[i] = 0;
    }
    
    visits.forEach(v => {
      const time = v.startTime || v.visitStartTime;
      if (!time) return;
      
      const hour = parseInt(time.split(':')[0]);
      if (!isNaN(hour) && hour >= 0 && hour < 24) {
        hourlyDistribution[hour]++;
      }
    });

    // Issue categories
    const issueCategories = {};
    visits.forEach(v => {
      if (v.visitEvaluation?.issueOccurred === 'yes' && v.visitEvaluation?.issueCategory) {
        const cat = v.visitEvaluation.issueCategory;
        issueCategories[cat] = (issueCategories[cat] || 0) + 1;
      }
    });

    return {
      totalVisits: total,
      homeVisits: homeV.length,
      siteVisits: siteV.length,
      slaMetVisits: slaMet,
      slaPercentage: slaMetPct,
      issueVisits: issueCount,
      deviationVisits: deviationCount,
      ecrfCompleted: ecrfDone,
      ecrfPercentage: ecrfPct,
      sourceFinalized: sourceDone,
      sourcePercentage: sourcePct,
      totalKm: r1(totalKm),
      avgKmPerVisit,
      totalTravelTime: Math.round(totalTravelTime),
      avgTravelTimePerVisit: total > 0 ? Math.round(totalTravelTime / total) : 0,
      totalVisitTime: Math.round(totalVisitTime),
      avgVisitTimePerVisit: total > 0 ? Math.round(totalVisitTime / total) : 0,
      performanceScore,
      overdueCount,
      capaOpenedCount,
      screenFailCount,
      dropPatientCount,
      aeSaeCount,
      dcfCount,
      missedCount,
      deviationPct,
      monthlyData,
      weeklyData,
      dailyDistribution,
      hourlyDistribution,
      issueCategories
    };
  }, [visits, passedStats]);

  const downloadExcel = () => {
    if (!statistics) return;

    const summaryData = [{
      'Çalışan': employee.name,
      'Toplam Vizit': statistics.totalVisits,
      'Ev Viziti': statistics.homeVisits,
      'Hastane Viziti': statistics.siteVisits,
      'Performans Puanı': statistics.performanceScore,
      'SLA %': statistics.slaPercentage,
      'SLA Karşılanan': statistics.slaMetVisits,
      'Gecikme': statistics.overdueCount,
      'eCRF %': statistics.ecrfPercentage,
      'eCRF Tamamlanan': statistics.ecrfCompleted,
      'Kaynak %': statistics.sourcePercentage,
      'Kaynak Tamamlanan': statistics.sourceFinalized,
      'Sorun Sayısı': statistics.issueVisits,
      'Protokol Sapması': statistics.deviationVisits,
      'Sapma %': statistics.deviationPct,
      'CAPA Açılan': statistics.capaOpenedCount,
      'Screen Fail': statistics.screenFailCount,
      'Drop Patient': statistics.dropPatientCount,
      'AE/SAE': statistics.aeSaeCount,
      'DCF': statistics.dcfCount,
      'Kaçırılan Vizit': statistics.missedCount,
      'Toplam KM': statistics.totalKm,
      'Ortalama KM/Vizit': statistics.avgKmPerVisit,
      'Toplam Seyahat (dk)': statistics.totalTravelTime,
      'Ortalama Seyahat/Vizit (dk)': statistics.avgTravelTimePerVisit,
      'Toplam Vizit Süresi (dk)': statistics.totalVisitTime,
      'Ortalama Vizit Süresi (dk)': statistics.avgVisitTimePerVisit
    }];

    const visitDetails = visits.map(v => {
      const ev = v.visitEvaluation || {};
      return {
        'Tarih': v.date || v.scheduledDate || v.firstVisitDate || '',
        'Vizit Tipi': v.visitType === 'home' ? 'Ev' : 'Hastane',
        'Hasta ID': v.patientId || '',
        'Vizit No': v.visitNumber || '',
        'Başlangıç': v.visitStartTime || v.startTime || '',
        'Bitiş': v.visitEndTime || v.endTime || '',
        'Süre (dk)': ((parseInt(ev.visitDurationHours) || 0) * 60 + (parseInt(ev.visitDurationMinutes) || 0)) || '',
        'KM': parseFloat(ev.travelDistanceBefore) || '',
        'SLA Hedef': ev.slaTarget || '',
        'Süreç Bitiş': ev.processEndDate || '',
        'SLA Karşılandı': ev.slaTarget && (v.date || v.firstVisitDate) && ev.processEndDate ? 
          (Math.ceil((new Date(ev.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(ev.slaTarget) ? 'Evet' : 'Hayır') : '',
        'Sorun': ev.issueOccurred === 'yes' ? 'Evet' : 'Hayır',
        'Sorun Kategorisi': ev.issueCategory || '',
        'Protokol Sapması': ev.protocolDeviation === 'yes' ? 'Evet' : 'Hayır',
        'eCRF Tamamlanma': ev.ecrfCompletionDate || '',
        'Kaynak Tamamlanma': ev.sourceDocCompletionDate || '',
        'CAPA': ev.capaOpened === 'yes' ? 'Evet' : 'Hayır',
        'Screen Fail': ev.screenFail === 'yes' ? 'Evet' : 'Hayır',
        'Drop Patient': ev.dropPatient === 'yes' ? 'Evet' : 'Hayır',
        'AE/SAE': ev.wasAeSaeTaken === 'yes' ? 'Evet' : 'Hayır',
        'DCF': ev.dcfCreated === 'yes' ? 'Evet' : 'Hayır'
      };
    });

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(summaryData);
    const ws2 = XLSX.utils.json_to_sheet(visitDetails);
    
    XLSX.utils.book_append_sheet(wb, ws1, 'Özet');
    XLSX.utils.book_append_sheet(wb, ws2, 'Vizit Detayları');
    
    XLSX.writeFile(wb, `${employee.name}_İstatistikler.xlsx`);
    toast.success('Excel dosyası indirildi');
  };

  if (!employee) {
    return null;
  }

  if (loading) {
    return (
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <FaArrowLeft /> Geri
          </button>
          <h1>{employee.name}</h1>
        </div>
        <div className={styles.emptyState}>
          <FaExclamationTriangle className={styles.emptyIcon} />
          <p>Seçilen tarih aralığında veri bulunamadı</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const monthlyChartData = Object.entries(statistics.monthlyData)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, data]) => ({
      month: month.substring(5) + '/' + month.substring(2, 4),
      'Ev Viziti': data.home,
      'Hastane Viziti': data.site,
      'Toplam': data.total,
      'Sorun': data.issues
    }));

  const weeklyChartData = Object.entries(statistics.weeklyData)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([week, count]) => ({
      week: week.substring(5),
      'Vizit Sayısı': count
    }));

  const dailyChartData = [
    { day: 'Paz', count: statistics.dailyDistribution[0] },
    { day: 'Pzt', count: statistics.dailyDistribution[1] },
    { day: 'Sal', count: statistics.dailyDistribution[2] },
    { day: 'Çar', count: statistics.dailyDistribution[3] },
    { day: 'Per', count: statistics.dailyDistribution[4] },
    { day: 'Cum', count: statistics.dailyDistribution[5] },
    { day: 'Cmt', count: statistics.dailyDistribution[6] }
  ];

  const hourlyChartData = Object.entries(statistics.hourlyDistribution)
    .filter(([_, count]) => count > 0)
    .map(([hour, count]) => ({
      hour: `${hour}:00`,
      'Vizit Sayısı': count
    }));

  const visitTypeData = [
    { name: 'Ev Viziti', value: statistics.homeVisits },
    { name: 'Hastane Viziti', value: statistics.siteVisits }
  ];

  const performanceRadarData = [
    { metric: 'SLA', value: statistics.slaPercentage, fullMark: 100 },
    { metric: 'eCRF', value: statistics.ecrfPercentage, fullMark: 100 },
    { metric: 'Kaynak', value: statistics.sourcePercentage, fullMark: 100 },
    { metric: 'Sorunsuz', value: Math.round(((statistics.totalVisits - statistics.issueVisits) / statistics.totalVisits) * 100), fullMark: 100 },
    { metric: 'Sapmasız', value: Math.round(100 - statistics.deviationPct), fullMark: 100 }
  ];

  return (
    <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <FaArrowLeft /> Geri
        </button>
        <div className={styles.headerInfo}>
          <h1 className={styles.employeeName}>{employee.name}</h1>
          <span className={styles.employeeTitle}>Saha Koordinatörü İstatistikleri</span>
        </div>
        <button className={styles.downloadButton} onClick={downloadExcel}>
          <FaDownload /> Excel İndir
        </button>
      </div>

      {/* Date Filter */}
      <div className={styles.dateFilter}>
        <FaCalendarAlt className={styles.dateIcon} />
        <input 
          type="date" 
          value={dateStart} 
          onChange={e => setDateStart(e.target.value)}
          className={styles.dateInput}
        />
        <span className={styles.dateSeparator}>—</span>
        <input 
          type="date" 
          value={dateEnd} 
          onChange={e => setDateEnd(e.target.value)}
          className={styles.dateInput}
        />
        <button 
          className={styles.dateResetButton}
          onClick={() => {
            setDateStart('');
            setDateEnd('');
          }}
        >
          Tümü
        </button>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={`${styles.summaryCard} ${styles.performanceCard}`}>
          <div className={styles.cardIcon}>
            <FaTachometerAlt />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.performanceScore}</div>
            <div className={styles.cardLabel}>Performans Puanı</div>
            <div className={styles.cardBadge} style={{
              background: statistics.performanceScore >= 80 ? '#c6f6d5' : statistics.performanceScore >= 50 ? '#fefcbf' : '#fed7d7',
              color: statistics.performanceScore >= 80 ? '#22543d' : statistics.performanceScore >= 50 ? '#744210' : '#9b2c2c'
            }}>
              {statistics.performanceScore >= 80 ? 'Mükemmel' : statistics.performanceScore >= 50 ? 'İyi' : 'Gelişmeli'}
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.visitsCard}`}>
          <div className={styles.cardIcon}>
            <FaCalendarAlt />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.totalVisits}</div>
            <div className={styles.cardLabel}>Toplam Vizit</div>
            <div className={styles.cardDetail}>
              <FaHome /> {statistics.homeVisits} · <FaHospital /> {statistics.siteVisits}
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.slaCard}`}>
          <div className={styles.cardIcon}>
            <FaCheckCircle />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.slaPercentage}%</div>
            <div className={styles.cardLabel}>SLA Karşılanma</div>
            <div className={styles.cardDetail}>
              {statistics.slaMetVisits} karşılandı · {statistics.overdueCount} gecikme
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.issuesCard}`}>
          <div className={styles.cardIcon}>
            <FaExclamationTriangle />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.issueVisits}</div>
            <div className={styles.cardLabel}>Sorun</div>
            <div className={styles.cardDetail}>
              {statistics.deviationVisits} protokol sapması · {statistics.missedCount} kaçırılan
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.distanceCard}`}>
          <div className={styles.cardIcon}>
            <FaMapMarkerAlt />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.totalKm} km</div>
            <div className={styles.cardLabel}>Toplam Mesafe</div>
            <div className={styles.cardDetail}>
              Ort: {statistics.avgKmPerVisit} km/vizit
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.timeCard}`}>
          <div className={styles.cardIcon}>
            <FaClock />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{Math.round(statistics.totalVisitTime / 60)}h</div>
            <div className={styles.cardLabel}>Toplam Vizit Süresi</div>
            <div className={styles.cardDetail}>
              Ort: {statistics.avgVisitTimePerVisit} dk/vizit
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.ecrfCard}`}>
          <div className={styles.cardIcon}>
            <FaFileAlt />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.ecrfPercentage}%</div>
            <div className={styles.cardLabel}>eCRF Tamamlanma</div>
            <div className={styles.cardDetail}>
              {statistics.ecrfCompleted} / {statistics.totalVisits} vizit
            </div>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.sourceCard}`}>
          <div className={styles.cardIcon}>
            <FaChartLine />
          </div>
          <div className={styles.cardContent}>
            <div className={styles.cardValue}>{statistics.sourcePercentage}%</div>
            <div className={styles.cardLabel}>Kaynak Kesinleşme</div>
            <div className={styles.cardDetail}>
              {statistics.sourceFinalized} / {statistics.totalVisits} vizit
            </div>
          </div>
        </div>

        {/* Additional Quality Metrics */}
        {(statistics.capaOpenedCount > 0 || statistics.screenFailCount > 0 || statistics.dropPatientCount > 0) && (
          <>
            <div className={`${styles.summaryCard} ${styles.qualityCard}`}>
              <div className={styles.cardIcon}>
                <FaExclamationTriangle />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardValue}>{statistics.capaOpenedCount}</div>
                <div className={styles.cardLabel}>CAPA Açılan</div>
                <div className={styles.cardDetail}>
                  Kalite iyileştirme aksiyonları
                </div>
              </div>
            </div>

            {statistics.siteVisits > 0 && (
              <div className={`${styles.summaryCard} ${styles.screenCard}`}>
                <div className={styles.cardIcon}>
                  <FaExclamationTriangle />
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.cardValue}>{statistics.screenFailCount + statistics.dropPatientCount}</div>
                  <div className={styles.cardLabel}>Screen Fail / Drop</div>
                  <div className={styles.cardDetail}>
                    {statistics.screenFailCount} fail · {statistics.dropPatientCount} drop
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Charts Grid */}
      <div className={styles.chartsGrid}>
        {/* Performance Radar */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Performans Analizi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={performanceRadarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} />
              <Radar name="Performans" dataKey="value" stroke="#4299e1" fill="#4299e1" fillOpacity={0.6} />
              <Tooltip formatter={(value) => `${value}%`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Visit Type Distribution */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Vizit Tipi Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={visitTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {visitTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Trend */}
        {monthlyChartData.length > 0 && (
          <div className={`${styles.chartCard} ${styles.chartCardWide}`}>
            <h3 className={styles.chartTitle}>Aylık Vizit Trendi</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="Ev Viziti" stackId="1" stroke="#4299e1" fill="#4299e1" />
                <Area type="monotone" dataKey="Hastane Viziti" stackId="1" stroke="#9f7aea" fill="#9f7aea" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Weekly Workload */}
        {weeklyChartData.length > 0 && (
          <div className={`${styles.chartCard} ${styles.chartCardWide}`}>
            <h3 className={styles.chartTitle}>Haftalık İş Yükü (Son 12 Hafta)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={weeklyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Vizit Sayısı" stroke="#68d391" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Daily Distribution */}
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Günlük Dağılım</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" name="Vizit Sayısı" radius={[8, 8, 0, 0]}>
                {dailyChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Hourly Distribution */}
        {hourlyChartData.length > 0 && (
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Saatlik Dağılım</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="Vizit Sayısı" fill="#f6ad55" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeStatistics;
